import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import type { ReactNode } from 'react';
import { cn } from './cn';

/**
 * Toasts.
 *
 * For confirmations that need to be seen and not acted on — "certificate
 * verified", "report generated". Deliberately NOT for errors that a form
 * caused: those belong next to the field, where the user is looking and where
 * they will still be after the toast has gone.
 *
 * The region is a live region, so a confirmation reaches somebody using a
 * screen reader. `polite` rather than `assertive`: a success message that
 * interrupts what is being read is worse than one that waits its turn.
 */
export type ToastTone = 'success' | 'info' | 'warning' | 'danger';

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
  detail?: string;
}

const TONE: Record<ToastTone, { box: string; mark: string }> = {
  success: { box: 'border-success-600/25 bg-success-100 text-success-600', mark: '✓' },
  info: { box: 'border-info-600/25 bg-info-100 text-info-600', mark: 'i' },
  warning: { box: 'border-warning-600/25 bg-warning-100 text-warning-600', mark: '!' },
  danger: { box: 'border-danger-600/25 bg-danger-100 text-danger-600', mark: '✕' },
};

const ToastContext = createContext<{
  toast: (tone: ToastTone, message: string, detail?: string) => void;
} | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const toast = useCallback((tone: ToastTone, message: string, detail?: string) => {
    const id = next.current++;
    setToasts((t) => [...t, { id, tone, message, detail }]);
    // Four seconds: long enough to read two lines, short enough not to sit
    // over the table the user went back to.
    setTimeout(() => dismiss(id), 4000);
  }, [dismiss]);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed right-4 top-4 z-50 flex w-80 flex-col gap-2"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto flex items-start gap-2.5 rounded-card border px-3.5 py-2.5',
              'shadow-dropdown animate-toast-in',
              TONE[t.tone].box,
            )}
          >
            <span aria-hidden className="mt-px text-body font-bold leading-5">{TONE[t.tone].mark}</span>
            <div className="min-w-0 flex-1">
              <p className="text-body font-medium">{t.message}</p>
              {t.detail && <p className="mt-0.5 text-caption font-normal text-neutral-600">{t.detail}</p>}
            </div>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
              className="shrink-0 rounded p-0.5 opacity-60 hover:opacity-100"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Returns a no-op outside a provider rather than throwing.
 *
 * A missing toast host must never be the reason a working save appears to
 * fail. The confirmation is the least important part of the interaction.
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  return ctx ?? { toast: () => undefined };
}

/** Announces a message to assistive tech without drawing anything. */
export function LiveRegion({ message }: { message: string | null }) {
  const [held, setHeld] = useState<string | null>(null);
  useEffect(() => { setHeld(message); }, [message]);
  return <span role="status" aria-live="polite" className="sr-only">{held}</span>;
}
